import type { JSX } from 'preact';
import { useComputed, useSignal } from '@preact/signals';
import { useCallback } from 'preact/hooks';
import { RotatedRibbon } from 'components/atoms/RotatedRibbon.tsx';
import { Icon } from 'components/atoms/Icon.tsx';
import { Pill } from 'components/atoms/Pill.tsx';
import { Badge } from 'components/atoms/Badge.tsx';
import { VideoBadge } from 'components/atoms/VideoBadge.tsx';
import { multiselectOffers } from 'components/bazar/signals';
import { FavoriteButton } from 'components/atoms/FavoriteButton';
import { OfferOptions } from 'components/offer/OfferOptions';
import { RejectedOverlay } from 'components/offer/card/RejectedOverlay';
import { generateOfferDetailLink, urlEncoder } from 'utils/url.ts';
import Link from 'components/atoms/Link';
import { isCWLEnabled } from 'lib/cwl/utils';
import { DOTHelper } from 'lib/dot/DOTHelper.ts';
import { filters as globalFilters } from 'signals/filters';
import { localFiltersToAnalyticsFilters } from 'lib/dot/utils.ts';
import { favoriteOffers } from 'signals/favoriteOffers';
import { MultiselectCheckbox } from 'components/offer/card/MultiselectCheckbox.tsx';
import { OfferCard } from 'components/offer/card/OfferCard';
import { isInactive } from 'components/offer/utils/status';
import { localize } from 'i18n/localize.ts';
import { isOfferAbleToTop, type OfferData, type WhereSold } from 'service/offer/model';
import { cn } from 'utils/cn';
import './OfferCard.less';
import { MessengerButtonContainer } from 'components/offer/MessengerButtonContainer';

const $t = localize();

export type Props = {
	analyticsPosition?: string;
	forceTopped?: boolean;
	isAdmin?: boolean;
	lazyImage?: boolean;
	multiselectInputProps?: Omit<JSX.IntrinsicElements['input'], 'onClick'> & {
		onClick: (event: JSX.TargetedMouseEvent<HTMLInputElement>, offer?: OfferData) => void;
	};
	noLink?: boolean;
	offer: OfferData;
	showBadgeNew?: boolean;
	showEditActions?: boolean;
	showFavoriteButton?: boolean;
	showInactive?: boolean;
	showMultiselect?: boolean;
	showReserved?: boolean;
	showToppedRibbon?: boolean;
	showVideo?: boolean;
	showVideoStatus?: boolean;
	voucherCount?: number;
	onOfferDelete?: (event: Event, offer: Partial<OfferData>, reason?: WhereSold) => void;
	onOfferToggleVisibility?: (event: Event, offer: Partial<OfferData>, reason?: WhereSold) => void;
	onOfferReserve?: (event: Event, offer: Partial<OfferData>) => void;
};

export function OfferCardWithOverlays({
	analyticsPosition,
	forceTopped = false,
	lazyImage,
	multiselectInputProps,
	noLink = false,
	offer,
	showBadgeNew = true,
	showEditActions = false,
	showFavoriteButton = true,
	showInactive = false,
	showMultiselect = false,
	showReserved = true,
	showToppedRibbon = true,
	showVideo = true,
	showVideoStatus = false,
	isAdmin = false,
	voucherCount,
	onOfferDelete,
	onOfferToggleVisibility,
	onOfferReserve,
}: Props) {
	const filtersWatchdogTimestamp = useComputed(() => globalFilters.value?.watchdog_timestamp?.seoName ?? '');
	const isDropdownOpen = useSignal(false);
	const isStatusInactive = isInactive(offer.status);
	const onClickCallback = useCallback(
		(event: JSX.TargetedMouseEvent<HTMLInputElement>) => {
			if (multiselectInputProps?.onClick) multiselectInputProps?.onClick(event, offer);
		},
		[multiselectInputProps, offer],
	);
	const urlTimestampDate = filtersWatchdogTimestamp.value && new Date(filtersWatchdogTimestamp.value);
	const offerDate = offer.createDate && new Date(offer.createDate);

	const linkParams =
		(offer?.otherLocalitiesCount ?? 0) > 0 && offer.locality?.offerLocalityId
			? { lokalita: String(offer.locality?.offerLocalityId) }
			: undefined;
	const offerCardProps = {
		offer,
		lazyImage,
		showVideoStatus,
		isAdmin,
	};

	const hasTopButton = isAdmin && isOfferAbleToTop(offer) && !isDropdownOpen.value;
	const hasVideo =
		showVideo &&
		Boolean(offer.videos?.length || offer?.videosTotalCount) &&
		!offer.videos?.some((video) => video.status === 'processing' || video.status === 'error');
	const hasReserved = showReserved && offer.isReserved;
	const hasNewOffer = showBadgeNew && offerDate && urlTimestampDate && offerDate > urlTimestampDate;
	const hasOfferInactive = isStatusInactive && showInactive;
	const hasTopped = showToppedRibbon && offer.topped;

	return (
		<div class="relative @container group/card">
			{noLink ? (
				<OfferCard {...offerCardProps} />
			) : (
				<Link
					cwlActive={isCWLEnabled()}
					classNameOverride=""
					href={generateOfferDetailLink(offer, linkParams)}
					onClick={(event: any) => {
						if (isDropdownOpen.peek()) {
							event.preventDefault();
							return;
						}

						const _filters = globalFilters.peek();
						// If we click on offer at previous page after offer appending, then update
						// page number in url to get back to correct page with clicked offer in history
						if (offer.loadedOnPageNumber && _filters.page.value !== offer.loadedOnPageNumber) {
							const newUrl = urlEncoder({
								...globalFilters.peek(),
								page: {
									value: offer.loadedOnPageNumber,
									seoName: offer.loadedOnPageNumber.toString(),
								},
							});
							history.replaceState({ ...history.state }, '', newUrl);
						}

						if (!analyticsPosition) return;
						const searchType = DOTHelper.getStore().searchType;

						DOTHelper.setOfferDetailMeta({
							...(_filters && { filters: localFiltersToAnalyticsFilters(_filters) }),
							...(searchType && { searchType }),
							saved: DOTHelper.sanitizeBoolean(favoriteOffers.value?.[offer.id]),
							advertPosition: analyticsPosition,
						});
					}}
				>
					<OfferCard {...offerCardProps} />
				</Link>
			)}
			{/* Overlay for interactive elements is placed outside the <a> tag to prevent event propagation issues
			(e.g. unintended redirects or onClick triggers). Uses pointer-events: none on the overlay and
			pointer-events: auto on interactive children. More info: https://gitlab.seznam.net/sbazar/ui-mono/-/wikis/%E2%9D%96-Components#info-about-interactive-layers-on-card */}
			<div class={cn('absolute top-0 w-full aspect-square pointer-events-none')}>
				{((hasTopped && !hasOfferInactive) || forceTopped) && (
					<div className="relative overflow-hidden w-full h-full">
						<RotatedRibbon className="top-2 -left-11" label="Top" />
					</div>
				)}
				{!multiselectOffers.value.enabled &&
					offer.approvalAdmin !== 'rejected' &&
					(hasOfferInactive || hasNewOffer) && (
						<div
							class={cn(
								'absolute z-10 top-2 left-2 @cardMd:top-3 @cardMd:left-3',
								showEditActions && '@cardMd:top-4',
							)}
						>
							{hasOfferInactive ? (
								<Badge type="dark-blue-transparent">{$t.offer.stateInactive}</Badge>
							) : (
								hasNewOffer && (
									<Badge type="primary" className="font-bold uppercase">
										{$t.offer.stateNew}
									</Badge>
								)
							)}
						</div>
					)}
				{hasVideo && (
					<div class="absolute z-10 bottom-2 right-2 @cardMd:bottom-3 @cardMd:right-3">
						<VideoBadge />
					</div>
				)}
				{offer.approvalAdmin === 'rejected' && (
					<div class="absolute z-10 w-full h-full left-0 top-0 flex items-center justify-center">
						<RejectedOverlay />
					</div>
				)}
				{hasReserved && (
					<Badge
						type="white"
						className="@cardLg:hidden absolute z-above bottom-2 left-2 @cardMd:bottom-3 @cardMd:left-3 px-3 leading-4 pointer-events-none"
					>
						{$t.offer.stateReserved}
					</Badge>
				)}

				{/*
					Interactive elements go here
				*/}
				<div class="pointer-events-auto">
					<div class="absolute z-20 top-2 right-2 @cardMd:top-3 @cardMd:right-3 flex items-center gap-1 @cardMd:gap-3 pointer-events-none">
						{hasReserved && !hasOfferInactive && (
							<Badge
								type="white"
								className={cn('hidden @cardLg:flex h-8 px-3 leading-4 pointer-events-none')}
							>
								{$t.offer.stateReserved}
							</Badge>
						)}
						{!showEditActions && showFavoriteButton && (
							<FavoriteButton
								className="pointer-events-auto"
								offer={offer}
								analyticsPosition={analyticsPosition}
							/>
						)}
						{showEditActions && (
							<OfferOptions
								openerClassName="pointer-events-auto"
								voucherCount={voucherCount}
								offer={offer}
								onOfferDelete={onOfferDelete}
								onOfferToggleVisibility={onOfferToggleVisibility}
								onOfferReserve={onOfferReserve}
								onDropdownOpen={() => (isDropdownOpen.value = true)}
								onDropdownClose={() => (isDropdownOpen.value = false)}
							/>
						)}
					</div>
					{showMultiselect && (
						<MultiselectCheckbox
							className="top-2 left-2 @cardMd:top-4 @cardMd:left-3 absolute z-10"
							multiselectInputProps={multiselectInputProps}
							offerId={offer.id}
							signal={multiselectOffers}
							onClickCallback={onClickCallback}
						/>
					)}
					{hasTopButton && (
						<div class="absolute bottom-0 h-20 left-0 w-full bg-gradient-to-b from-[#00000000] to-[#00000080] rounded-2xl !pointer-events-none invisible @cardLg:group-hover/card:visible" />
					)}
					{hasTopButton && (
						<div class="md:invisible group-hover/card:visible absolute z-10 bottom-2.5 @cardLg:bottom-3.5 w-full text-center hidden @cardLg:group-hover/card:block">
							<Pill
								href={`${generateOfferDetailLink(offer)}/topovani`}
								as="a"
								pillType="tertiary"
								className="pointer-events-auto text-red hover:text-red-hover focus:text-red-hover font-bold inline-flex justify-center items-center py-[5px] pl-3 pr-5"
							>
								<Icon className="mr-2" type="star" fill size={20} />{' '}
								<span className="leading-5">{$t.offer.topButton}</span>
							</Pill>
						</div>
					)}
					<MessengerButtonContainer
						className="absolute z-10 bottom-2 right-2 @cardMd:bottom-3 @cardMd:right-3"
						offer={offer}
					/>
				</div>
			</div>
		</div>
	);
}
